import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

function BanksPage({ token }) {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState({ bankName: '', branch: '', notes: '' });

  useEffect(() => {
    loadBanks();
  }, [showInactive]);

  const loadBanks = async () => {
    try {
      setLoading(true);
      const url = showInactive
        ? `${CONFIG.API}/api/bills/banks?status=ALL`
        : `${CONFIG.API}/api/bills/banks`;
      const res = await axios.get(url, {
        headers: { authorization: `Bearer ${token}` }
      });
      setBanks(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm({ bankName: '', branch: '', notes: '' });
    setEditingBank(null);
    setShowForm(false);
  };

  const handleEditClick = (bank) => {
    setForm({
      bankName: bank.bankName || '',
      branch:   bank.branch   || '',
      notes:    typeof bank.notes === 'string' ? bank.notes : ''
    });
    setEditingBank(bank);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!form.bankName.trim()) { alert('Please enter the bank name'); return; }

    const payload = {
      bankName: form.bankName.trim(),
      branch:   form.branch.trim(),
      notes:    form.notes || null
    };

    try {
      if (editingBank) {
        await axios.put(
          `${CONFIG.API}/api/bills/banks/${editingBank.bankId}`,
          payload,
          { headers: { authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${CONFIG.API}/api/bills/banks`,
          payload,
          { headers: { authorization: `Bearer ${token}` } }
        );
      }
      resetForm();
      loadBanks();
    } catch (err) {
      const msg = err.response && err.response.data
        ? err.response.data
        : 'Error saving bank. Please try again.';
      alert(msg);
    }
  };

  const handleDeactivate = async (bank) => {
    if (!window.confirm(`Deactivate ${bank.bankName}? It will stop appearing in dropdowns, but old records keep it.`)) return;
    try {
      await axios.put(
        `${CONFIG.API}/api/bills/banks/${bank.bankId}/deactivate`,
        {},
        { headers: { authorization: `Bearer ${token}` } }
      );
      loadBanks();
    } catch (err) {
      alert('Error deactivating bank.');
    }
  };

  const handleReactivate = async (bank) => {
    try {
      await axios.put(
        `${CONFIG.API}/api/bills/banks/${bank.bankId}/reactivate`,
        {},
        { headers: { authorization: `Bearer ${token}` } }
      );
      loadBanks();
    } catch (err) {
      alert('Error reactivating bank.');
    }
  };

  return (
    <div className="content">

      {/* Summary cards */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Active banks</div>
          <div className="metric-value val-green">
            {banks.filter(b => b.status === 'ACTIVE').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Total banks</div>
          <div className="metric-value val-blue">
            {banks.length}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="section-header">
        <div className="section-title">Manage banks</div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New bank'}
        </button>
      </div>

      <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '14px' }}>
        Banks added here appear in the bank dropdown on every page (PCFC, discounting,
        realisation, forwards, import). One spelling per bank keeps records matched.
      </div>

      {/* New / Edit form */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '560px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingBank ? '✏️ Edit bank' : '🏦 New bank'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Bank name</label>
              <input name="bankName" value={form.bankName}
                onChange={handleChange} placeholder="e.g. SBIN" />
            </div>
            <div className="form-field">
              <label>Branch (optional)</label>
              <input name="branch" value={form.branch}
                onChange={handleChange} placeholder="e.g. Kochi" />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any additional notes..."
                rows={2}
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit}>
              {editingBank ? '✓ Update bank' : '✓ Save bank'}
            </button>
          </div>
        </div>
      )}

      {/* Show inactive toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
                    marginBottom: '12px' }}>
        <label style={{ fontSize: '13px', color: '#64748B', cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Show inactive banks
        </label>
      </div>

      {/* Banks table */}
      <div className="table-wrap">
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : banks.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No banks yet. Click "+ New bank" to add your first one.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Bank name</th>
                <th>Branch</th>
                <th>Notes</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {banks.map(b => (
                <tr key={b.bankId}>
                  <td>{b.bankName}</td>
                  <td>{b.branch || '—'}</td>
                  <td>{b.notes || '—'}</td>
                  <td>
                    <span className={`badge ${b.status === 'ACTIVE' ? 'badge-open' : 'badge-cancelled'}`}>
                      {b.status}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-secondary"
                      onClick={() => handleEditClick(b)}
                      style={{ marginRight: '6px' }}>
                      Edit
                    </button>
                    {b.status === 'ACTIVE' ? (
                      <button className="btn-danger"
                        onClick={() => handleDeactivate(b)}>
                        Deactivate
                      </button>
                    ) : (
                      <button className="btn-secondary"
                        onClick={() => handleReactivate(b)}>
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

export default BanksPage;